import * as React from "react";
import {
    getSingleUserActivityData,
    getSingleUserActivityRequest,
    MappedUserAccount,
    Notifications,
} from "modules/dashboard";
import { Button } from "../Button";
import { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Can } from "modules/auth";
import {
    CustomTable,
    OptionType,
    Pagination,
    RoleType,
    TableType,
    userRoles,
} from "modules/common";
import { getDisplayDateFormat } from "helpers/DatesHelper";
import { RootState } from "store";
import { ValueType } from "react-select";
import { SortingRule } from "react-table";
import { AccessibilityContext } from "views/AccessibilityContext";

interface Props {
    userInfo: MappedUserAccount;
    onDeactivateClick?: (uid: string, roleType: number, isDeactivate: boolean) => void;
    showActivity?: boolean;
}

const DEFAULT_PAGE_SIZE_OPTIONS = {
    value: "5",
    label: "5",
};

const PAGE_NEIGHBOURS = 1;

export const UserInfo: React.FC<Props> = ({
    userInfo: {
        uid,
        legalEntityName,
        mibpg,
        street,
        streetNumber,
        postalNumber,
        city,
        county,
        country,
        mobileNumber,
        email,
        notificationType,
        infoType,
        roleType,
        isActive,
        additionalMobileNumber,
    },
    onDeactivateClick,
    showActivity,
}) => {
    const dispatch = useDispatch();
    const userActivity = useSelector((state: RootState) => state.singleUserActivity);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState<OptionType>(DEFAULT_PAGE_SIZE_OPTIONS);
    const [sortBy, setSortBy] = useState<SortingRule<object>>();

    const tableColumns = [
        {
            Header: "DATUM I VRIJEME",
            id: "date",
            accessor: (data: any) => (
                <span className="date-time">{getDisplayDateFormat(data.createdAt, true)}</span>
            ),
        },
        {
            Header: "APLIKACIJA",
            accessor: "application",
        },
        {
            Header: "AKCIJA",
            id: "action",
            accessor: (data: any) => <span className="user-action">{data.userAction}</span>,
        },
        {
            Header: "KREIRAO",
            accessor: "createdByName",
            className: "created-by-header",
        },
    ];

    const handlePageSizeChange = (value: ValueType<OptionType, false>) => {
        value && setPageSize(value);
        setCurrentPage(1);
    };

    const handlePageClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
        const page = Number(parseInt(event.currentTarget.id));
        setCurrentPage(page);
    };

    const handleNexPageClick = () => {
        setCurrentPage(prev => prev + 1);
    };

    const handlePrevPageClick = () => {
        setCurrentPage(prev => prev - 1);
    };

    const handleFirstPageClick = () => {
        setCurrentPage(1);
    };

    const handleLastPageClick = () => {
        setCurrentPage(
            Math.ceil(
                userActivity.userActivityLogCount /
                    parseInt(pageSize?.value ?? DEFAULT_PAGE_SIZE_OPTIONS.value)
            )
        );
    };

    const handlePageSelect = (page: number) => {
        setCurrentPage(page);
    };

    useEffect(() => {
        const size = parseInt(pageSize?.value ?? DEFAULT_PAGE_SIZE_OPTIONS.value);
        dispatch(getSingleUserActivityRequest());
        dispatch(
            getSingleUserActivityData({
                pageNumber: currentPage,
                pageSize: size,
                userRoleFilter: [],
                actionTypeFilter: [],
                applicationTypeFilter: [],
                search: "",
                dateFrom: new Date(0),
                dateTo: new Date(),
                userUid: uid,
                sortField: sortBy?.id,
                descending: sortBy?.desc,
            })
        );
    }, [currentPage, dispatch, pageSize?.value, sortBy]);

    const { lowVision } = useContext(AccessibilityContext);
    return (
        <div>
            {roleType === RoleType.Farmer && (
                <div className="userInfo-section business-container userInfo-grid">
                    <div className="f-column grid-column-1">
                        <span className="userInfo-label">Naziv</span>
                        <span className="business-text">{legalEntityName}</span>
                    </div>
                    <div className="f-column grid-column-2">
                        <span className="userInfo-label">Mibpg</span>
                        <span className="business-text">{mibpg}</span>
                    </div>
                    <div className="f-column grid-column-3">
                        <span className="userInfo-label">Adresa</span>
                        <span className="userInfo-text">
                            {street} {streetNumber}
                        </span>
                        <span className="userInfo-text">
                            {postalNumber} {city}
                        </span>
                        <span className="userInfo-text">{county}</span>
                        <span className="userInfo-text">{country}</span>
                    </div>
                </div>
            )}

            <div className="userInfo-separator" />
            <div className="userInfo-section userInfo-grid">
                <div className="f-column grid-column-1">
                    {roleType !== RoleType.Farmer ? (
                        <>
                            <span className="userInfo-label">Adresa</span>
                            <span className="userInfo-text">
                                {street} {streetNumber}
                            </span>
                            <span className="userInfo-text">
                                {postalNumber} {city}
                            </span>
                            <span className="userInfo-text">{county}</span>
                            <span className="userInfo-text">{country}</span>
                        </>
                    ) : (
                        <>
                            <span className="userInfo-label">Kontakt broj</span>
                            <span className="userInfo-text">{additionalMobileNumber}</span>
                        </>
                    )}
                </div>
                <div className="f-column grid-column-2">
                    <span className="userInfo-label">SEMIS broj</span>
                    <span className="userInfo-text">{mobileNumber}</span>
                </div>
                <div className="f-column grid-column-3">
                    <span className="userInfo-label">E-mail adresa</span>
                    <a href={`mailto:${email}`} className="userInfo-email">
                        {email}
                    </a>
                </div>
            </div>
            <div className="userInfo-separator" />
            <div className="app-info-section userInfo-grid">
                <div className="f-column">
                    <span className="userInfo-label">Način obavještavanja</span>
                    <Notifications notificationType={notificationType} />
                </div>
                <div className="f-column">
                    <span className="subscriptions-label">Predbilježbe na obavijesti</span>
                    {infoType.map((v, index) => (
                        <span key={index} className="subscriptions-text">
                            {v}
                        </span>
                    ))}
                </div>
            </div>
            <div className="userInfo-separator" />
            <div className="app-info-section">
                <span className="userInfo-label">Uloga</span>
                <div className="userInfo-roles">
                    {Object.values(userRoles).find(role => role.value === roleType)?.label ?? ""}
                </div>
            </div>
            <div className="userInfo-separator" />
            {showActivity ? (
                <div className="userInfo-activity">
                    <div className="userInfo-activity-table">
                        <CustomTable
                            data={userActivity.data ?? []}
                            columns={tableColumns}
                            loading={false}
                            tableType={TableType.AdministrationTable}
                            setSortBy={setSortBy}
                            flex
                        />
                        <Pagination
                            className="mt-24"
                            currentPage={currentPage}
                            numberOfPages={Math.ceil(
                                userActivity.userActivityLogCount /
                                    parseInt(pageSize?.value ?? DEFAULT_PAGE_SIZE_OPTIONS.value)
                            )}
                            pageNeighbours={PAGE_NEIGHBOURS}
                            pageSize={pageSize ?? DEFAULT_PAGE_SIZE_OPTIONS}
                            onPageSizeChange={handlePageSizeChange}
                            handlePageClick={handlePageClick}
                            handlePageSelect={handlePageSelect}
                            next={handleNexPageClick}
                            prev={handlePrevPageClick}
                            first={handleFirstPageClick}
                            last={handleLastPageClick}
                        />
                    </div>
                </div>
            ) : (
                <div className="deactivate-section">
                    <Button
                        className="mr-32"
                        type="button"
                        variant={isActive ? "warning-ghost" : "primary-ghost"}
                        size="xsmall"
                        onClick={() =>
                            onDeactivateClick
                                ? onDeactivateClick(uid, roleType ?? 0, isActive)
                                : null
                        }
                        lowVision={lowVision}
                    >
                        <span>{isActive && `DE`}AKTIVIRAJ KORISNIKA</span>
                    </Button>
                    <div className={lowVision? "low-vision-white-text":"user-deactivate"}>
                        Deaktivacijom korisnika deaktivira se korisnički račun na sustavu
                        e-Savjetnik
                    </div>
                </div>
            )}
        </div>
    );
};
